
import React, { useState, useEffect, useMemo } from 'react';
import { StudentConnection, ClassRoom, UserProfile, Message, Announcement, AcademicYear, WeeklyLog, Homework, StudentHomeworkStatus, AttendanceRecord, CalendarEvent, TutorLog, HomeworkTask, FamilyConnectPrompt, Kudo, Organization, Gender, AssessmentEvent, AssessmentResult, PreReportedAbsence, StuckLog } from '../types';
import { TabMenuItem, Header, TabMenu, OrgSwitcher, WelcomeBanner, Avatar, DropdownMenu } from './common';
import { HomeworkHub } from './homework';
import { ChatWindow } from './chat';
import { ParentKudosView, SendTeacherKudoModal } from './kudos';
import { CharacterCompassView, FamilyConnectWidget } from './family';
import { ParentCheckinHistoryView } from './shared';
import { SmartCalendar } from './attendance';
import { EditProfileModal, WeeklyCheckinModal, ReportAbsenceModal, AddChildModal, EditStudentModal, PolicyModal } from './modals';
import { HelpModal } from './help';
import { useTranslate } from '../hooks/useTranslate';
import { useCheckinTranslation } from '../hooks/useCheckinTranslation';
import { CheckinTranslationBar } from './CheckinTranslationBar';
import { ParentAssessmentList } from './assessments';
import { isFeatureEnabled, isParentPolicyEnabled } from '../lib/features';
import { setActiveOrgApiKey } from '../lib/gemini';
import { 
    getInstructionalDatesToDate, 
    computeStudentAttendanceMetrics, 
    computeMonthlyAttendanceLedger 
} from '../lib/attendanceUtils';

// Persistent Seen Timestamp Helpers for Kudos and Exam Results
export const getStoredSeenTimestamp = (userId: string, studentId: string, type: 'kudos' | 'results'): number => {
  try {
    const val = localStorage.getItem(`edugravity_seen_${userId}_${studentId}_${type}`);
    if (val !== null) {
      return parseInt(val, 10) || 0;
    }
    // Baseline for first-time visits: 7 days ago, so recent items are flagged until seen
    const defaultBaseline = Date.now() - 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem(`edugravity_seen_${userId}_${studentId}_${type}`, defaultBaseline.toString());
    return defaultBaseline;
  } catch (e) {
    return Date.now() - 7 * 24 * 60 * 60 * 1000;
  }
};

export const setStoredSeenTimestamp = (userId: string, studentId: string, type: 'kudos' | 'results', timestamp: number) => {
  try {
    localStorage.setItem(`edugravity_seen_${userId}_${studentId}_${type}`, timestamp.toString());
  } catch (e) {
    // ignore
  }
};

const ParentChildView = ({ 
    student, cls, user, onBack, onLogout, onUpdateUser, allMessages, allAnnouncements, onSendMessage, onUpdateMessage, onDeleteMessage, allUsers, setViewingImageUrl, activeYear, myStudentsInActiveYear, allLogs, onSaveWeeklyLog, onMarkMessagesRead, allHomeworks, studentHomeworkStatuses, onUpdateStudentHomeworkStatus, onLogStuck, attendanceRecords, onReportAbsence, calendarEvents, t, tutorLogs, onGetTutorHelp, onTranslateTutorInteraction, familyConnectPrompts, allKudos, org, onSendKudo, onDeleteAccount, allStudents, assessmentEvents, assessmentResults, preReportedAbsences, stuckLogs, initialTab = 'homework', initialChatSubTab = 'group', seenMap, onMarkTabSeen 
}: { 
    student: StudentConnection, cls: ClassRoom, user: UserProfile, onBack: () => void, onLogout: () => void, onUpdateUser: (userId: string, data: Partial<UserProfile>) => void, allMessages: Message[], allAnnouncements: Announcement[], onSendMessage: (message: Omit<Message, 'id' | 'timestamp' | 'readBy'>) => void, onUpdateMessage: (messageId: string, newText: string, targetLanguage: string) => void, onDeleteMessage: (messageId: string) => void, allUsers: UserProfile[], setViewingImageUrl: (url: string | null) => void, activeYear: AcademicYear | undefined, myStudentsInActiveYear: StudentConnection[], allLogs: WeeklyLog[], onSaveWeeklyLog: (log: Omit<WeeklyLog, 'id'>) => Promise<void>, onMarkMessagesRead: (messageIds: string[]) => void, allHomeworks: Homework[], studentHomeworkStatuses: StudentHomeworkStatus[], onUpdateStudentHomeworkStatus: (studentId: string, homeworkId: string, taskId: string, isComplete: boolean) => void, onLogStuck: (studentId: string, homeworkId: string, taskId: string) => void, attendanceRecords: AttendanceRecord[], onReportAbsence: (absence: {date: string, reason: string, attachment?: string, id?: string}, isDelete?: boolean) => void, calendarEvents: CalendarEvent[], t: (key: string, reps?: any) => string, tutorLogs: TutorLog[], onGetTutorHelp: (studentId: string, homeworkId: string, taskId: string, task: HomeworkTask, studentName: string, existingLog: TutorLog | null, userInput?: string, triggerType?: 'initial' | 'concept' | 'example' | 'resources' | 'question') => Promise<void>, onTranslateTutorInteraction: (logId: string, interactionIndex: number, text: string, targetLanguage: string) => Promise<void>, familyConnectPrompts: FamilyConnectPrompt[], allKudos: Kudo[], org: Organization | undefined, onSendKudo: (kudo: Omit<Kudo, 'id' | 'timestamp'>) => Promise<void>; onDeleteAccount: () => void, allStudents: StudentConnection[], assessmentEvents: AssessmentEvent[], assessmentResults: AssessmentResult[], preReportedAbsences: PreReportedAbsence[], stuckLogs?: StuckLog[], initialTab?: 'homework' | 'chat' | 'reports' | 'calendar' | 'compass' | 'kudos' | 'results', initialChatSubTab?: 'group' | 'direct', seenMap?: Record<string, { kudos?: number; results?: number }>, onMarkTabSeen?: (studentId: string, tab: 'kudos' | 'results') => void 
}) => {
    const [activeTab, setActiveTab] = useState<'homework' | 'chat' | 'reports' | 'calendar' | 'compass' | 'kudos' | 'results'>(initialTab);
    const [chatSubTab, setChatSubTab] = useState<'group' | 'direct'>(initialChatSubTab);
    const [showWeeklyCheckinModal, setShowWeeklyCheckinModal] = useState(false);
    const [showReportAbsenceModal, setShowReportAbsenceModal] = useState(false);
    const [absenceToEdit, setAbsenceToEdit] = useState<PreReportedAbsence | null>(null);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [showTeacherKudoModal, setShowTeacherKudoModal] = useState(false);

    // Dynamic translation hook for Weekly Check-in & History based on parent profile language
    const checkinTrans = useCheckinTranslation(user.language);
    
    // Get all teachers for this class
    const classTeachers = allUsers.filter(u => cls.teacherIds.includes(u.id));
    // Default language for chat translation
    const primaryTeacher = classTeachers[0];
    const teacherLanguage = primaryTeacher?.language || 'en';

    // Daily Direct Message Rate Limiting
    const dailyDirectMessageLimit = org?.parentPolicies?.dailyDirectMessageLimit ?? 5;
    const todayDateString = new Date().toDateString();
    const parentDirectMessagesSentToday = allMessages.filter(m => {
        if (m.senderId !== user.id) return false;
        if (m.studentId !== student.id) return false;
        const msgDate = m.timestamp instanceof Date ? m.timestamp.toDateString() : new Date(m.timestamp).toDateString();
        return msgDate === todayDateString;
    }).length;

    const isDirectChatDisabled = Boolean(user.directChatDisabled || student.directChatDisabled);
    
    // Bucket 1: Class Group Chat (one-way broadcast from school/teachers)
    const classGroupMessages = allMessages.filter(m => {
        if (m.classId !== cls.id) return false;
        return !m.studentId;
    });

    // Bucket 2: Direct 1:1 Chat (between School and Parent about this specific student)
    const directStudentMessages = allMessages.filter(m => {
        if (m.classId !== cls.id) return false;
        return m.studentId === student.id;
    });

    // Unread count for Direct Chat
    const unreadDirectCount = directStudentMessages.filter(m => m.senderId !== user.id && !m.readBy?.includes(user.id)).length;
    const unreadGroupCount = classGroupMessages.filter(m => m.senderId !== user.id && !m.readBy?.includes(user.id)).length;
    
    const studentAnnouncements = allAnnouncements.filter(a => a.academicYearId === activeYear?.id && (a.classIds.length === 0 || a.classIds.includes(cls.id)));
    const canShowWeeklyCheckin = activeYear && myStudentsInActiveYear.some(s => s.approvalStatus === 'approved') && isParentPolicyEnabled(org, 'allowParentWeeklyCheckin', 'weeklyCheckin');

    // Filter attendance records for the specific student here.
    const studentAttendanceRecords = attendanceRecords.filter(r => r.studentId === student.id);
    const studentReportedAbsences = preReportedAbsences.filter(r => r.studentId === student.id);
    
    // Kudos logic
    const studentPersonalKudos = allKudos.filter(k => k.studentId === student.id && !k.teacherId);
    const classPublicKudos = allKudos.filter(k => k.classId === cls.id && !k.teacherId);
    const parentSentKudos = allKudos.filter(k => k.senderId === user.id);
    const classStudents = allStudents.filter(s => s.classId === cls.id);

    // --- Check-in Frequency Logic ---
    const getReportingWeekMonday = () => {
        const now = new Date();
        const day = now.getDay(); // 0=Sun, 1=Mon, ..., 4=Thu, 5=Fri, 6=Sat
        
        // Window is open Thursday(4) through Sunday(0)
        const isWindowOpen = day === 4 || day === 5 || day === 6 || day === 0;
        
        const d = new Date(now);
        const daysToSubtract = (day + 6) % 7;
        d.setDate(d.getDate() - daysToSubtract);
        
        return {
            dateStr: d.toISOString().split('T')[0],
            isWindowOpen
        };
    };

    const { dateStr: reportingDate, isWindowOpen } = getReportingWeekMonday();
    
    // Ensure only one submission per week (identified by its Monday start date)
    const hasSubmittedCheckin = allLogs.some(log => log.studentId === student.id && log.date === reportingDate);
    
    const isArchived = student.academicYearId !== activeYear?.id;

    // Helper to format the date range for UI (Monday to Sunday)
    const getFormattedReportingRange = () => {
        const start = new Date(reportingDate);
        const end = new Date(reportingDate);
        end.setDate(end.getDate() + 6);
        return `${start.toLocaleDateString(undefined, {month: 'short', day: 'numeric'})} - ${end.toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}`;
    };

    const [calendarViewMode, setCalendarViewMode] = useState<'ledger' | 'calendar'>('ledger');

    // Calculate official student instructional dates up to today
    const instructionalDates = useMemo(() => {
        if (!activeYear?.startDate) return [];
        return getInstructionalDatesToDate(
            activeYear.startDate,
            activeYear.endDate,
            activeYear.offSaturdays || [2, 4],
            calendarEvents,
            cls.id
        );
    }, [activeYear, calendarEvents, cls.id]);

    // Student cumulative attendance metrics
    const attendanceMetrics = useMemo(() => {
        return computeStudentAttendanceMetrics(student.id, studentAttendanceRecords, instructionalDates);
    }, [student.id, studentAttendanceRecords, instructionalDates]);

    // Student monthly ledger
    const monthlyLedger = useMemo(() => {
        return computeMonthlyAttendanceLedger(student.id, studentAttendanceRecords, instructionalDates);
    }, [student.id, studentAttendanceRecords, instructionalDates]);

    // Calculate Attendance Stats (backward-compatible fallback + enhanced)
    const attendanceStats = useMemo(() => {
        return {
            present: attendanceMetrics.present,
            absent: attendanceMetrics.absent,
            late: attendanceMetrics.late,
            total: attendanceMetrics.effectiveTotalDays,
            percentage: attendanceMetrics.percentage,
            statusThreshold: attendanceMetrics.statusThreshold,
            unrecordedDays: attendanceMetrics.unrecordedDays
        };
    }, [attendanceMetrics]);


    const handleParentSendMessage = (text: string, originalText: string, imageUrl?: string) => {
        onSendMessage({
            classId: cls.id,
            studentId: student.id,
            senderId: user.id,
            senderName: user.name,
            senderRole: 'parent',
            text,
            originalText,
            originalLanguage: user.language,
            imageUrl,
            academicYearId: cls.academicYearId,
        });
    };

    const handleEditAbsence = (absence: PreReportedAbsence) => {
        setAbsenceToEdit(absence);
        setShowReportAbsenceModal(true);
    };

    // Tab badges calculation
    const studentStuckLogs = (stuckLogs || []).filter(l => l.studentId === student.id);
    const activeStuckCount = studentStuckLogs.filter(log => {
        const status = studentHomeworkStatuses.find(st => st.homeworkId === log.homeworkId && st.studentId === student.id);
        return !status?.completedTaskIds?.includes(log.taskId);
    }).length;

    const classHomeworks = allHomeworks.filter(h => h.classId === cls.id);
    const threeDaysAgoStr = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const recentHomeworks = classHomeworks.filter(h => h.date >= threeDaysAgoStr);
    let pendingHwTasks = 0;
    recentHomeworks.forEach(hw => {
        const status = studentHomeworkStatuses.find(st => st.homeworkId === hw.id && st.studentId === student.id);
        const completed = status?.completedTaskIds?.length || 0;
        pendingHwTasks += Math.max(0, hw.tasks.length - completed);
    });

    const lastSeenKudos = seenMap?.[student.id]?.kudos ?? getStoredSeenTimestamp(user.id, student.id, 'kudos');
    const recentKudosCount = activeTab === 'kudos' 
        ? 0 
        : studentPersonalKudos.filter(k => new Date(k.timestamp).getTime() > lastSeenKudos).length;

    const lastSeenResults = seenMap?.[student.id]?.results ?? getStoredSeenTimestamp(user.id, student.id, 'results');
    const classPublishedEvents = assessmentEvents.filter(e => e.status === 'PUBLISHED' && e.classId === cls.id);
    const classPublishedEventMap = new Map(classPublishedEvents.map(e => [e.id, e]));
    const recentAssessmentCount = activeTab === 'results' 
        ? 0 
        : assessmentResults.filter(r => {
            if (r.studentId !== student.id || r.isWithheld) return false;
            const evt = classPublishedEventMap.get(r.assessmentId);
            if (!evt) return false;
            const resultTime = evt.publishedAt ? new Date(evt.publishedAt).getTime() : (evt.createdAt ? new Date(evt.createdAt).getTime() : new Date(evt.date).getTime());
            return resultTime > lastSeenResults;
        }).length;

    const totalUnreadMessages = unreadDirectCount + unreadGroupCount;
    
    const todayDateStr = new Date().toISOString().split('T')[0];
    const todayAttendance = studentAttendanceRecords.find(r => r.date === todayDateStr);
    const isAbsentOrLateToday = todayAttendance?.status === 'absent' || todayAttendance?.status === 'late';
    const isCheckinDue = Boolean(canShowWeeklyCheckin && isWindowOpen && !hasSubmittedCheckin && student.approvalStatus === 'approved');

    const parentTabs: TabMenuItem[] = useMemo(() => {
        const tabs: TabMenuItem[] = [];
        if (isParentPolicyEnabled(org, 'allowParentViewHomework', 'homework')) {
            tabs.push({ 
                id: 'homework', 
                label: t('homework'), 
                icon: 'home_work',
                badge: activeStuckCount > 0 ? 'Stuck' : (pendingHwTasks > 0 ? pendingHwTasks : undefined),
                badgeColor: activeStuckCount > 0 ? 'bg-amber-600 animate-pulse' : 'bg-indigo-600'
            });
        }
        if (isFeatureEnabled(org, 'chat') || isFeatureEnabled(org, 'announcements')) {
            tabs.push({ 
                id: 'chat', 
                label: t('chat'), 
                icon: 'forum',
                badge: totalUnreadMessages > 0 ? totalUnreadMessages : undefined,
                badgeColor: unreadDirectCount > 0 ? 'bg-rose-500 animate-pulse' : 'bg-indigo-600'
            });
        }
        if (isParentPolicyEnabled(org, 'allowParentViewAssessments', 'assessments')) {
            tabs.push({ 
                id: 'results', 
                label: t('exam_results'), 
                icon: 'grade',
                badge: recentAssessmentCount > 0 ? recentAssessmentCount : undefined,
                badgeColor: 'bg-emerald-600'
            });
        }
        if (isParentPolicyEnabled(org, 'allowParentViewCalendar', 'calendar')) {
            tabs.push({ 
                id: 'calendar', 
                label: t('attendance'), 
                icon: 'how_to_reg',
                dot: isAbsentOrLateToday,
                dotColor: 'bg-orange-500'
            });
        }
        const isWeeklyCheckinActive = isParentPolicyEnabled(org, 'allowParentWeeklyCheckin', 'weeklyCheckin');
        const studentWeeklyLogs = allLogs.filter(l => l.studentId === student.id);
        if (isWeeklyCheckinActive || studentWeeklyLogs.length > 0) {
            tabs.push({ 
                id: 'reports', 
                label: isWeeklyCheckinActive ? t('reports_checkin') : 'Check-in History', 
                icon: 'bar_chart',
                badge: (isWeeklyCheckinActive && isCheckinDue) ? 'Due' : undefined,
                badgeColor: 'bg-purple-600'
            });
        }
        if (isParentPolicyEnabled(org, 'allowParentViewKudos', 'kudos')) {
            tabs.push({ 
                id: 'kudos', 
                label: 'Kudos', 
                icon: 'military_tech',
                badge: recentKudosCount > 0 ? recentKudosCount : undefined,
                badgeColor: 'bg-amber-500'
            });
        }
        if (isParentPolicyEnabled(org, 'enableFamilyConnect', 'familyConnect')) {
            tabs.push({ id: 'compass', label: 'Character', icon: 'explore' });
        }
        return tabs;
    }, [org, t, activeStuckCount, pendingHwTasks, totalUnreadMessages, unreadDirectCount, recentAssessmentCount, isAbsentOrLateToday, isCheckinDue, recentKudosCount, allLogs, student.id]);

    useEffect(() => {
        if (parentTabs.length > 0 && !parentTabs.some(tab => tab.id === activeTab)) {
            setActiveTab(parentTabs[0].id as any);
        }
    }, [parentTabs, activeTab]);

    useEffect(() => {
        if (activeTab === 'kudos' && onMarkTabSeen) {
            onMarkTabSeen(student.id, 'kudos');
        } else if (activeTab === 'results' && onMarkTabSeen) {
            onMarkTabSeen(student.id, 'results');
        }
    }, [activeTab, student.id, onMarkTabSeen]);

    return (
      <>
        <div className="flex flex-col h-full bg-white">
            <Header
                title={student.childName}
                subtitle={cls.name}
                user={user}
                onBack={onBack}
                onLogout={onLogout}
                onEditProfile={() => setIsEditingProfile(true)}
            />
            
            <TabMenu 
                items={parentTabs} 
                activeTab={activeTab} 
                onSelectTab={(id) => {
                    const nextTab = id as any;
                    setActiveTab(nextTab);
                    if ((nextTab === 'kudos' || nextTab === 'results') && onMarkTabSeen) {
                        onMarkTabSeen(student.id, nextTab);
                    }
                }} 
            />
    
            <div className="flex-1 overflow-y-auto bg-gray-50">
                {activeTab === 'homework' && (
                    <HomeworkHub 
                        student={student}
                        user={user}
                        allHomeworks={allHomeworks.filter(h => h.classId === cls.id)}
                        studentHomeworkStatuses={studentHomeworkStatuses}
                        onUpdateStudentHomeworkStatus={onUpdateStudentHomeworkStatus}
                        onLogStuck={onLogStuck}
                        t={t}
                        tutorLogs={tutorLogs}
                        onGetTutorHelp={onGetTutorHelp}
                        onTranslateTutorInteraction={onTranslateTutorInteraction}
                        org={org}
                    />
                )}
                {activeTab === 'chat' && (
                    <div className="flex flex-col h-full">
                        {/* Two-Bucket Sub-tabs */}
                        <div className="bg-white border-b px-4 py-2 flex items-center justify-between gap-2 shadow-xs">
                            <div className="flex bg-gray-100 p-1 rounded-xl w-full max-w-md">
                                <button
                                    type="button"
                                    onClick={() => setChatSubTab('group')}
                                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                        chatSubTab === 'group'
                                            ? 'bg-white text-indigo-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    <span className="material-icons-round text-base">campaign</span>
                                    <span>Class Announcements</span>
                                    {unreadGroupCount > 0 && (
                                        <span className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.2 rounded-full font-bold">
                                            {unreadGroupCount}
                                        </span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setChatSubTab('direct')}
                                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                        chatSubTab === 'direct'
                                            ? 'bg-white text-indigo-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    <span className="material-icons-round text-base">forum</span>
                                    <span>Direct Chat (1:1)</span>
                                    {unreadDirectCount > 0 && (
                                        <span className="bg-rose-500 text-white text-[10px] px-1.5 py-0.2 rounded-full font-bold animate-pulse">
                                            {unreadDirectCount}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Chat Window Rendering */}
                        <div className="flex-1 overflow-hidden">
                            {chatSubTab === 'group' ? (
                                <ChatWindow 
                                    messages={classGroupMessages}
                                    announcements={studentAnnouncements}
                                    kudos={classPublicKudos}
                                    onSendMessage={handleParentSendMessage}
                                    user={user}
                                    targetLanguage={teacherLanguage}
                                    allUsers={allUsers}
                                    setViewingImageUrl={setViewingImageUrl}
                                    onUpdateMessage={(id, text) => onUpdateMessage(id, text, teacherLanguage)}
                                    onDeleteMessage={onDeleteMessage}
                                    students={classStudents}
                                    isDirectChat={false}
                                    readOnly={isArchived}
                                />
                            ) : (
                                <ChatWindow 
                                    messages={directStudentMessages}
                                    announcements={[]}
                                    kudos={studentPersonalKudos}
                                    onSendMessage={handleParentSendMessage}
                                    user={user}
                                    targetLanguage={teacherLanguage}
                                    allUsers={allUsers}
                                    setViewingImageUrl={setViewingImageUrl}
                                    onUpdateMessage={(id, text) => onUpdateMessage(id, text, teacherLanguage)}
                                    onDeleteMessage={onDeleteMessage}
                                    students={[student]}
                                    isDirectChat={true}
                                    isDirectChatDisabled={isDirectChatDisabled || !isParentPolicyEnabled(org, 'allowParentInitiatedChat', 'chat')}
                                    dailyLimit={dailyDirectMessageLimit}
                                    dailyCount={parentDirectMessagesSentToday}
                                    allowAttachments={true}
                                    readOnly={isArchived}
                                />
                            )}
                        </div>
                    </div>
                )}
                {activeTab === 'results' && (
                    <ParentAssessmentList 
                        events={assessmentEvents.filter(e => e.classId === cls.id)}
                        results={assessmentResults.filter(r => r.studentId === student.id)}
                        t={t}
                        org={org}
                        userLanguage={user.language}
                    />
                )}
                {activeTab === 'kudos' && (
                    <div className="flex flex-col h-full">
                        {isParentPolicyEnabled(org, 'allowParentSendKudos', 'kudos') && (
                            <div className="p-4 pb-0">
                                <button 
                                    onClick={() => setShowTeacherKudoModal(true)}
                                    className="w-full bg-indigo-100 text-indigo-700 py-3 rounded-xl font-bold shadow-sm border border-indigo-200 flex items-center justify-center gap-2 hover:bg-indigo-200 transition-colors"
                                >
                                    <span className="material-icons-round">volunteer_activism</span>
                                    Send Appreciation to Teachers
                                </button>
                            </div>
                        )}
                        <ParentKudosView
                            student={student}
                            receivedKudos={studentPersonalKudos}
                            sentKudos={parentSentKudos}
                            org={org}
                            t={t}
                            allHomeworks={allHomeworks}
                            studentHomeworkStatuses={studentHomeworkStatuses}
                            tutorLogs={tutorLogs}
                        />
                    </div>
                )}
                {activeTab === 'compass' && (
                    <CharacterCompassView
                        user={user}
                        prompts={familyConnectPrompts.filter(p => p.studentId === student.id)}
                        t={t}
                        showCheckinNudge={canShowWeeklyCheckin && isWindowOpen && !hasSubmittedCheckin && !isArchived}
                        onNudgeClick={() => setShowWeeklyCheckinModal(true)}
                    />
                )}
                {activeTab === 'reports' && (
                    <div className="p-4 space-y-4">
                        {canShowWeeklyCheckin && (
                            <div className="bg-white rounded-xl p-6 shadow-sm border space-y-4">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="font-bold text-gray-800 text-xl">{checkinTrans.tCheckin('weekly_checkin')}</h3>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {checkinTrans.tCheckin('week_colon', 'Week:')} <span className="font-bold text-indigo-600">{getFormattedReportingRange()}</span>
                                        </p>
                                    </div>
                                    <div className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold border border-indigo-100">
                                        {checkinTrans.tCheckin('one_submission_per_week')}
                                    </div>
                                </div>

                                {/* Translation Control Bar */}
                                <CheckinTranslationBar
                                    selectedLang={checkinTrans.selectedLang}
                                    onSelectLang={checkinTrans.setSelectedLang}
                                    showOriginal={checkinTrans.showOriginal}
                                    onToggleOriginal={checkinTrans.toggleShowOriginal}
                                    isTranslating={checkinTrans.isTranslating}
                                    targetLangLabel={checkinTrans.targetLangLabel}
                                />

                                {hasSubmittedCheckin ? (
                                    <div className="bg-green-50 text-green-700 p-4 rounded-xl flex items-center gap-3 border border-green-200 shadow-sm">
                                        <span className="material-icons-round text-2xl">check_circle</span>
                                        <div>
                                            <p className="font-bold">{checkinTrans.tCheckin('thank_you')}</p>
                                            <p className="text-sm">{checkinTrans.tCheckin('submitted_msg')}</p>
                                        </div>
                                    </div>
                                ) : isWindowOpen ? (
                                    <button 
                                        onClick={() => setShowWeeklyCheckinModal(true)} 
                                        className="w-full bg-indigo-600 text-white py-4 rounded-xl font-bold shadow-md flex items-center justify-center gap-2 hover:bg-indigo-700 transition-transform active:scale-95"
                                    >
                                        <span className="material-icons-round">rate_review</span>
                                        {checkinTrans.tCheckin('submit_weekly_checkin')}
                                    </button>
                                ) : (
                                    <div className="bg-gray-100 text-gray-500 p-4 rounded-xl text-center border border-gray-200">
                                        <span className="material-icons-round text-3xl mb-2 block opacity-50">event_busy</span>
                                        <p className="font-bold text-gray-700">{checkinTrans.tCheckin('submission_window_closed')}</p>
                                        <p className="text-sm mt-1">{checkinTrans.tCheckin('submission_window_closed_msg')}</p>
                                        <p className="text-xs font-bold text-indigo-500 mt-2 uppercase tracking-wide">{checkinTrans.tCheckin('submission_window_info')}</p>
                                    </div>
                                )}
                            </div>
                        )}
                        <ParentCheckinHistoryView
                            logs={allLogs.filter(l => l.studentId === student.id)}
                            t={t}
                            userLanguage={checkinTrans.selectedLang}
                        />
                    </div>
                )}
                 {activeTab === 'calendar' && (
                    <div className="p-4 space-y-4">
                        {/* View Switcher: Tabular Ledger vs Monthly Calendar */}
                        <div className="flex bg-gray-100 p-1 rounded-xl shadow-xs">
                            <button
                                type="button"
                                onClick={() => setCalendarViewMode('ledger')}
                                className={`flex-1 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                                    calendarViewMode === 'ledger'
                                        ? 'bg-white text-indigo-700 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <span className="material-icons-round !text-base">table_chart</span>
                                Tabular Ledger & Totals
                            </button>
                            <button
                                type="button"
                                onClick={() => setCalendarViewMode('calendar')}
                                className={`flex-1 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                                    calendarViewMode === 'calendar'
                                        ? 'bg-white text-indigo-700 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <span className="material-icons-round !text-base">calendar_month</span>
                                Monthly Calendar
                            </button>
                        </div>

                        {calendarViewMode === 'ledger' ? (
                            <div className="space-y-4">
                                {/* Running Attendance Summary Card */}
                                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200/80 space-y-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-3">
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-base">Student Attendance Standing</h3>
                                            <p className="text-xs text-gray-500">
                                                Running attendance % calculated against official working days up to today
                                            </p>
                                        </div>
                                        <div>
                                            {attendanceMetrics.statusThreshold === 'healthy' ? (
                                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-200">
                                                    <span className="material-icons-round !text-sm text-green-600">check_circle</span>
                                                    Good Standing (&ge; 75% Board Requirement)
                                                </span>
                                            ) : attendanceMetrics.statusThreshold === 'warning' ? (
                                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
                                                    <span className="material-icons-round !text-sm text-amber-600">warning</span>
                                                    Attendance Warning (Below 75% Board Threshold)
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-red-50 text-red-700 border border-red-200">
                                                    <span className="material-icons-round !text-sm text-red-600">error</span>
                                                    Critical Shortage (&lt; 65%)
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
                                        <div className="p-3 bg-indigo-50/70 rounded-xl border border-indigo-100 col-span-2 sm:col-span-1 flex flex-col justify-center">
                                            <div className={`text-3xl font-black ${
                                                attendanceMetrics.percentage >= 75 ? 'text-indigo-700' : attendanceMetrics.percentage >= 65 ? 'text-amber-600' : 'text-red-600'
                                            }`}>
                                                {attendanceMetrics.percentage.toFixed(1)}%
                                            </div>
                                            <div className="text-[11px] text-indigo-900 font-bold uppercase tracking-wider mt-1">Running %</div>
                                        </div>
                                        <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                                            <div className="text-2xl font-bold text-gray-800">{attendanceMetrics.effectiveTotalDays}</div>
                                            <div className="text-[11px] text-gray-500 font-medium uppercase mt-1">Working Days</div>
                                        </div>
                                        <div className="p-3 bg-green-50/50 rounded-xl border border-green-100">
                                            <div className="text-2xl font-bold text-green-700">{attendanceMetrics.present}</div>
                                            <div className="text-[11px] text-green-700 font-medium uppercase mt-1">{t('present')}</div>
                                        </div>
                                        <div className="p-3 bg-yellow-50/50 rounded-xl border border-yellow-100">
                                            <div className="text-2xl font-bold text-yellow-700">{attendanceMetrics.late}</div>
                                            <div className="text-[11px] text-yellow-700 font-medium uppercase mt-1">{t('late')}</div>
                                        </div>
                                        <div className="p-3 bg-red-50/50 rounded-xl border border-red-100">
                                            <div className="text-2xl font-bold text-red-600">{attendanceMetrics.absent}</div>
                                            <div className="text-[11px] text-red-600 font-medium uppercase mt-1">{t('absent')}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Tabular Monthly Breakdown & Total to Date */}
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden">
                                    <div className="p-4 border-b bg-gray-50/70 flex flex-wrap items-center justify-between gap-2">
                                        <div>
                                            <h4 className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
                                                <span className="material-icons-round text-indigo-600 !text-lg">receipt_long</span>
                                                Monthly Attendance Passbook
                                            </h4>
                                            <p className="text-xs text-gray-500">
                                                Month-by-month instructional breakdown with cumulative total to date
                                            </p>
                                        </div>
                                        <div className="text-xs text-gray-500 font-medium">
                                            Academic Session: {activeYear?.name || 'Current'}
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse text-xs">
                                            <thead>
                                                <tr className="bg-gray-100/70 text-gray-600 font-semibold border-b">
                                                    <th className="p-3">Month</th>
                                                    <th className="p-3 text-center">Working Days</th>
                                                    <th className="p-3 text-center">Present (P)</th>
                                                    <th className="p-3 text-center">Late (L)</th>
                                                    <th className="p-3 text-center">Absent (A)</th>
                                                    <th className="p-3 text-center">Month %</th>
                                                    <th className="p-3 text-center">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {monthlyLedger.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={7} className="p-6 text-center text-gray-400">
                                                            No instructional days recorded yet in the current academic year.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    monthlyLedger.map((row) => (
                                                        <tr key={row.monthKey} className="hover:bg-gray-50/70 transition-colors">
                                                            <td className="p-3 font-semibold text-gray-800 flex items-center gap-1.5">
                                                                <span className="material-icons-round text-gray-400 !text-sm">calendar_today</span>
                                                                {row.monthLabel}
                                                            </td>
                                                            <td className="p-3 text-center font-medium text-gray-700">{row.workingDays}</td>
                                                            <td className="p-3 text-center font-bold text-green-600">{row.present}</td>
                                                            <td className="p-3 text-center font-bold text-yellow-600">{row.late}</td>
                                                            <td className="p-3 text-center font-bold text-red-500">{row.absent}</td>
                                                            <td className="p-3 text-center">
                                                                <span className={`inline-block px-2 py-0.5 rounded-md font-bold text-[11px] ${
                                                                    row.percentage >= 75 ? 'bg-green-100 text-green-800' : row.percentage >= 65 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                    {row.percentage.toFixed(0)}%
                                                                </span>
                                                            </td>
                                                            <td className="p-3 text-center">
                                                                {row.percentage >= 75 ? (
                                                                    <span className="text-[11px] text-green-700 font-semibold">Good</span>
                                                                ) : row.percentage >= 65 ? (
                                                                    <span className="text-[11px] text-amber-700 font-semibold">Borderline</span>
                                                                ) : (
                                                                    <span className="text-[11px] text-red-600 font-semibold">Shortage</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                            {/* Final Total Row (Sum Until That Time) */}
                                            <tfoot>
                                                <tr className="bg-indigo-50/90 font-bold border-t-2 border-indigo-200 text-indigo-950">
                                                    <td className="p-3.5 uppercase tracking-wide flex items-center gap-1">
                                                        <span className="material-icons-round !text-base text-indigo-700">summarize</span>
                                                        Total to Date (Annual Sum)
                                                    </td>
                                                    <td className="p-3.5 text-center text-sm font-extrabold text-indigo-900">{attendanceMetrics.effectiveTotalDays} Days</td>
                                                    <td className="p-3.5 text-center text-sm font-extrabold text-green-700">{attendanceMetrics.present}</td>
                                                    <td className="p-3.5 text-center text-sm font-extrabold text-yellow-700">{attendanceMetrics.late}</td>
                                                    <td className="p-3.5 text-center text-sm font-extrabold text-red-600">{attendanceMetrics.absent}</td>
                                                    <td className="p-3.5 text-center">
                                                        <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-black shadow-xs ${
                                                            attendanceMetrics.percentage >= 75 ? 'bg-green-600 text-white' : attendanceMetrics.percentage >= 65 ? 'bg-amber-500 text-white' : 'bg-red-600 text-white'
                                                        }`}>
                                                            {attendanceMetrics.percentage.toFixed(1)}%
                                                        </span>
                                                    </td>
                                                    <td className="p-3.5 text-center">
                                                        <span className="text-xs font-bold text-indigo-900">
                                                            {attendanceMetrics.statusThreshold === 'healthy' ? 'Eligible' : attendanceMetrics.statusThreshold === 'warning' ? 'Review' : 'Deficit'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                    <div className="p-3 bg-gray-50 border-t text-[11px] text-gray-500 flex items-start gap-1.5">
                                        <span className="material-icons-round !text-sm text-gray-400 mt-0.5">info</span>
                                        <span>
                                            Working days count official instructional days from academic year start ({activeYear?.startDate || 'Session Start'}) up to today, excluding scheduled holidays and non-instructional Saturdays. Late marks count as full attendance.
                                        </span>
                                    </div>
                                </div>

                                {isParentPolicyEnabled(org, 'allowParentReportAbsence', 'attendance') && (
                                    <button 
                                        type="button"
                                        onClick={() => { setAbsenceToEdit(null); setShowReportAbsenceModal(true); }} 
                                        className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-bold shadow-md flex items-center justify-center gap-2 transition-colors"
                                    >
                                       <span className="material-icons-round">edit_calendar</span>
                                       {t('report_absence')}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {isParentPolicyEnabled(org, 'allowParentReportAbsence', 'attendance') && (
                                    <button 
                                        type="button"
                                        onClick={() => { setAbsenceToEdit(null); setShowReportAbsenceModal(true); }} 
                                        className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-bold shadow-md flex items-center justify-center gap-2 transition-colors"
                                    >
                                       <span className="material-icons-round">edit_calendar</span>
                                       {t('report_absence')}
                                    </button>
                                )}
                                <SmartCalendar 
                                    user={user} 
                                    records={studentAttendanceRecords} 
                                    events={calendarEvents} 
                                    preReportedAbsences={studentReportedAbsences}
                                    onEditAbsence={handleEditAbsence}
                                    t={t} 
                                    setViewingImageUrl={setViewingImageUrl} 
                                    userLanguage={user.language}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
        {isEditingProfile && (
            <EditProfileModal
                user={user}
                onClose={() => setIsEditingProfile(false)}
                onSave={onUpdateUser}
                t={t}
                onDeleteAccount={onDeleteAccount}
            />
        )}
        {showWeeklyCheckinModal && activeYear && (
            <WeeklyCheckinModal 
                onClose={() => setShowWeeklyCheckinModal(false)}
                onSave={async (data: any) => {
                    await onSaveWeeklyLog({
                        ...data,
                        studentId: student.id,
                        classId: cls.id,
                        academicYearId: activeYear.id,
                        date: reportingDate // Use the calculated Monday date to enforce one-per-week
                    });
                    setShowWeeklyCheckinModal(false);
                }}
                t={t}
                studentName={student.childName}
                userLanguage={checkinTrans.selectedLang || user.language}
            />
        )}
        {showReportAbsenceModal && (
            <ReportAbsenceModal
                existingAbsence={absenceToEdit}
                onClose={() => { setShowReportAbsenceModal(false); setAbsenceToEdit(null); }}
                onSave={(data: any) => {
                    onReportAbsence(data);
                    setShowReportAbsenceModal(false);
                }}
                onDelete={(id: string) => {
                    onReportAbsence({ id } as any, true);
                    setShowReportAbsenceModal(false);
                }}
                t={t}
            />
        )}
        {showTeacherKudoModal && activeYear && (
            <SendTeacherKudoModal
                isOpen={showTeacherKudoModal}
                onClose={() => setShowTeacherKudoModal(false)}
                user={user}
                teachers={classTeachers}
                student={student}
                activeYear={activeYear}
                onSendKudo={onSendKudo}
                t={t}
            />
        )}
      </>
    );
};

// ... (ParentManagerProps and ParentManager follow, updating the onReportAbsence prop signature in ParentManagerProps)

type ParentManagerProps = {
  // ... existing props
  user: UserProfile;
  allUsers: UserProfile[];
  students: StudentConnection[];
  allStudents: StudentConnection[];
  classes: ClassRoom[];
  academicYears: AcademicYear[];
  allMessages: Message[];
  onMarkMessagesRead: (messageIds: string[]) => void;
  allAnnouncements: Announcement[];
  allLogs: WeeklyLog[];
  onAddStudent: (childName: string, classCode: string, gender?: Gender, photoUrl?: string) => Promise<{success: boolean, message?: string}>;
  onSendMessage: (message: Omit<Message, 'id' | 'timestamp' | 'readBy'>) => void;
  onUpdateMessage: (messageId: string, newText: string, targetLanguage: string) => void;
  onDeleteMessage: (messageId: string) => void;
  onSaveWeeklyLog: (log: Omit<WeeklyLog, 'id'>) => Promise<void>;
  onLogout: () => void;
  onUpdateUser: (userId: string, data: Partial<UserProfile>) => void;
  onUpdateStudent: (studentId: string, data: Partial<StudentConnection>) => void;
  onDeleteStudent: (studentId: string) => void;
  setViewingImageUrl: (url: string | null) => void;
  orgs: Organization[];
  allHomeworks: Homework[];
  studentHomeworkStatuses: StudentHomeworkStatus[];
  onUpdateStudentHomeworkStatus: (studentId: string, homeworkId: string, taskId: string, isComplete: boolean) => void;
  onLogStuck: (studentId: string, homeworkId: string, taskId: string) => void;
  attendanceRecords: AttendanceRecord[];
  onReportAbsence: (absence: { date: string, reason: string, attachment?: string, id?: string }, studentId: string, isDelete?: boolean) => void;
  calendarEvents: CalendarEvent[];
  tutorLogs: TutorLog[];
  onGetTutorHelp: (studentId: string, homeworkId: string, taskId: string, task: HomeworkTask, studentName: string, existingLog: TutorLog | null, userInput?: string, triggerType?: 'initial' | 'concept' | 'example' | 'resources' | 'question') => Promise<void>;
  onTranslateTutorInteraction: (logId: string, interactionIndex: number, text: string, targetLanguage: string) => Promise<void>;
  familyConnectPrompts: FamilyConnectPrompt[];
  onGenerateFamilyConnectPrompt: () => Promise<void>;
  allKudos: Kudo[];
  onSendKudo: (kudo: Omit<Kudo, 'id' | 'timestamp'>) => Promise<void>;
  onDeleteAccount: () => void;
  assessmentEvents: AssessmentEvent[];
  assessmentResults: AssessmentResult[];
  preReportedAbsences: PreReportedAbsence[];
  stuckLogs?: StuckLog[];
};

export const ParentManager = ({ 
  user, allUsers, students, allStudents, classes, academicYears, allMessages, onMarkMessagesRead, allAnnouncements, allLogs, onAddStudent, onSendMessage, onUpdateMessage, onDeleteMessage, onSaveWeeklyLog, onLogout, onUpdateUser, onUpdateStudent, onDeleteStudent, setViewingImageUrl, orgs, allHomeworks, studentHomeworkStatuses, onUpdateStudentHomeworkStatus, onLogStuck, attendanceRecords, onReportAbsence, calendarEvents, tutorLogs, onGetTutorHelp, onTranslateTutorInteraction, familyConnectPrompts, onGenerateFamilyConnectPrompt, allKudos, onSendKudo, onDeleteAccount, assessmentEvents, assessmentResults, preReportedAbsences, stuckLogs
}: ParentManagerProps) => {
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [initialTabForChild, setInitialTabForChild] = useState<'homework' | 'chat' | 'reports' | 'calendar' | 'compass' | 'kudos' | 'results'>('homework');
  const [initialChatSubTabForChild, setInitialChatSubTabForChild] = useState<'group' | 'direct'>('group');
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState<StudentConnection | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const t = useTranslate(user.language);

  // Persistent seen timestamps for Kudos and Exam Results per student
  const [seenMap, setSeenMap] = useState<Record<string, { kudos?: number; results?: number }>>(() => {
    const map: Record<string, { kudos?: number; results?: number }> = {};
    students.forEach(s => {
      map[s.id] = {
        kudos: getStoredSeenTimestamp(user.id, s.id, 'kudos'),
        results: getStoredSeenTimestamp(user.id, s.id, 'results'),
      };
    });
    return map;
  });

  const markTabSeen = (studentId: string, tab: 'kudos' | 'results') => {
    const now = Date.now();
    setStoredSeenTimestamp(user.id, studentId, tab, now);
    setSeenMap(prev => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {}),
        [tab]: now,
      }
    }));
  };

  // --- Multi-Org Logic ---
  const parentOrgIds = [...new Set(
    students
        .map(s => classes.find(c => c.id === s.classId)?.orgId)
        .filter((orgId): orgId is string => !!orgId)
  )];
  const parentOrgs = orgs.filter(o => parentOrgIds.includes(o.id));

  const [activeOrgId, setActiveOrgId] = useState<string | undefined>(user.orgId || parentOrgs[0]?.id);

  useEffect(() => {
    const newActiveOrgId = user.orgId || parentOrgs[0]?.id;
    if (newActiveOrgId) {
      setActiveOrgId(newActiveOrgId);
    }
  }, [user.orgId, students]); // Re-evaluate when user's persisted orgId changes or student list changes
  
  useEffect(() => {
    const activeOrg = orgs.find(o => o.id === activeOrgId);
    setActiveOrgApiKey(activeOrg?.customApiKey || null);
  }, [activeOrgId, orgs]);
  
  const handleSwitchOrg = (orgId: string) => {
    setActiveOrgId(orgId);
    // Persist the choice for the next session
    onUpdateUser(user.id, { orgId });
  };

  const getActiveYearForOrg = (orgId: string | undefined): AcademicYear | undefined => {
    if (!orgId) return undefined;
    const now = new Date();
    return academicYears
      .filter(y => y.orgId === orgId)
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
      .find(y => y.isActivated && new Date(y.startDate) <= now && new Date(y.endDate) >= now);
  };

  const activeYear = getActiveYearForOrg(activeOrgId);
  const myOrg = orgs.find(o => o.id === activeOrgId);
  
  const myStudentsInActiveOrg = students.filter(s => {
    const studentClass = classes.find(c => c.id === s.classId);
    return studentClass?.orgId === activeOrgId;
  });

  const myStudentsInActiveYear = activeYear 
    ? myStudentsInActiveOrg.filter(s => s.academicYearId === activeYear.id)
    : [];

  useEffect(() => {
    if (selectedStudentId) {
        const student = students.find(s => s.id === selectedStudentId);
        if (!student) return;

        const unreadMessageIds = allMessages
            .filter(m => m.classId === student.classId && m.senderId !== user.id && !m.readBy.includes(user.id) && (m.studentId === student.id || !m.studentId))
            .map(m => m.id);
        
        if (unreadMessageIds.length > 0) {
            onMarkMessagesRead(unreadMessageIds);
        }
    }
  }, [selectedStudentId, students, allMessages, user.id, onMarkMessagesRead]);

  const handleSaveStudent = async (studentId: string, data: Partial<StudentConnection>): Promise<void> => {
    await onUpdateStudent(studentId, data);
  };

  const handleOpenChild = (
    studentId: string, 
    tab: 'homework' | 'chat' | 'reports' | 'calendar' | 'compass' | 'kudos' | 'results' = 'homework', 
    chatSubTab: 'group' | 'direct' = 'group'
  ) => {
    if (tab === 'kudos' || tab === 'results') {
      markTabSeen(studentId, tab);
    }
    setSelectedStudentId(studentId);
    setInitialTabForChild(tab);
    setInitialChatSubTabForChild(chatSubTab);
  };

  // Helper to calculate comprehensive real activity for a child
  const getStudentActivity = (student: StudentConnection, studentOrg?: Organization) => {
    const orgToCheck = studentOrg || myOrg;

    // Feature and Policy checks
    const canViewHomework = isParentPolicyEnabled(orgToCheck, 'allowParentViewHomework', 'homework');
    const canUseAiTutor = isParentPolicyEnabled(orgToCheck, 'allowParentAITutor', 'aiTutor');
    const canUseChat = isFeatureEnabled(orgToCheck, 'chat');
    const canViewAnnouncements = canUseChat || isFeatureEnabled(orgToCheck, 'announcements');
    const canViewKudos = isParentPolicyEnabled(orgToCheck, 'allowParentViewKudos', 'kudos');
    const canViewAssessments = isParentPolicyEnabled(orgToCheck, 'allowParentViewAssessments', 'assessments');
    const canViewAttendance = isFeatureEnabled(orgToCheck, 'attendance') || isParentPolicyEnabled(orgToCheck, 'allowParentViewCalendar', 'calendar');
    const canWeeklyCheckin = isParentPolicyEnabled(orgToCheck, 'allowParentWeeklyCheckin', 'weeklyCheckin');

    // 1. Unread Direct Messages
    const unreadDirectCount = canUseChat ? allMessages.filter(m => 
      m.classId === student.classId && 
      m.studentId === student.id && 
      m.senderId !== user.id && 
      !m.readBy?.includes(user.id)
    ).length : 0;

    // 2. Unread Class Announcements / Group Messages
    const unreadGroupCount = canViewAnnouncements ? allMessages.filter(m => 
      m.classId === student.classId && 
      !m.studentId && 
      m.senderId !== user.id && 
      !m.readBy?.includes(user.id)
    ).length : 0;

    // 3. Stuck Logs on active homework
    const studentStuckLogs = (stuckLogs || []).filter(l => l.studentId === student.id);
    const activeStuckCount = (canViewHomework && canUseAiTutor) ? studentStuckLogs.filter(log => {
      const status = studentHomeworkStatuses.find(st => st.homeworkId === log.homeworkId && st.studentId === student.id);
      return !status?.completedTaskIds?.includes(log.taskId);
    }).length : 0;

    // 4. Pending Homework Tasks (assigned in the last 3 days)
    let pendingHwTasks = 0;
    if (canViewHomework) {
      const classHomeworks = allHomeworks.filter(h => h.classId === student.classId);
      const threeDaysAgoStr = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const recentHomeworks = classHomeworks.filter(h => h.date >= threeDaysAgoStr);
      recentHomeworks.forEach(hw => {
        const status = studentHomeworkStatuses.find(st => st.homeworkId === hw.id && st.studentId === student.id);
        const completed = status?.completedTaskIds?.length || 0;
        pendingHwTasks += Math.max(0, hw.tasks.length - completed);
      });
    }

    // 5. Unseen Kudos (disappears the moment parent visits the Kudos tab)
    const lastSeenKudos = seenMap[student.id]?.kudos ?? getStoredSeenTimestamp(user.id, student.id, 'kudos');
    const recentKudosCount = canViewKudos ? allKudos.filter(k => 
      k.studentId === student.id && 
      !k.teacherId && 
      new Date(k.timestamp).getTime() > lastSeenKudos
    ).length : 0;

    // 6. Unseen Assessment Results (disappears the moment parent visits the Exam Results tab)
    const lastSeenResults = seenMap[student.id]?.results ?? getStoredSeenTimestamp(user.id, student.id, 'results');
    const publishedEventMap = new Map(assessmentEvents.filter(e => e.status === 'PUBLISHED' && e.classId === student.classId).map(e => [e.id, e]));
    const recentAssessmentCount = canViewAssessments ? assessmentResults.filter(r => {
      if (r.studentId !== student.id || r.isWithheld) return false;
      const evt = publishedEventMap.get(r.assessmentId);
      if (!evt) return false;
      const resultTime = evt.publishedAt ? new Date(evt.publishedAt).getTime() : (evt.createdAt ? new Date(evt.createdAt).getTime() : new Date(evt.date).getTime());
      return resultTime > lastSeenResults;
    }).length : 0;

    // 7. Attendance Today
    const todayDateStr = new Date().toISOString().split('T')[0];
    const todayAttendance = attendanceRecords.find(r => r.studentId === student.id && r.date === todayDateStr);
    const markedAbsentToday = canViewAttendance && todayAttendance?.status === 'absent';
    const markedLateToday = canViewAttendance && todayAttendance?.status === 'late';

    // 8. Weekly Check-in status
    const now = new Date();
    const day = now.getDay();
    const isWindowOpen = day === 4 || day === 5 || day === 6 || day === 0;
    const d = new Date(now);
    const daysToSubtract = (day + 6) % 7;
    d.setDate(d.getDate() - daysToSubtract);
    const repDate = d.toISOString().split('T')[0];
    const hasSubmitted = allLogs.some(log => log.studentId === student.id && log.date === repDate);
    const isWeeklyCheckinDue = Boolean(canWeeklyCheckin && isWindowOpen && !hasSubmitted && student.approvalStatus === 'approved');

    const totalActivityItems = (unreadDirectCount > 0 ? 1 : 0) +
      (unreadGroupCount > 0 ? 1 : 0) +
      (activeStuckCount > 0 ? 1 : 0) +
      (pendingHwTasks > 0 ? 1 : 0) +
      (recentKudosCount > 0 ? 1 : 0) +
      (recentAssessmentCount > 0 ? 1 : 0) +
      (markedAbsentToday || markedLateToday ? 1 : 0) +
      (isWeeklyCheckinDue ? 1 : 0);

    return {
      unreadDirectCount,
      unreadGroupCount,
      totalUnread: unreadDirectCount + unreadGroupCount,
      activeStuckCount,
      pendingHwTasks,
      recentKudosCount,
      recentAssessmentCount,
      markedAbsentToday,
      markedLateToday,
      isWeeklyCheckinDue,
      totalActivityItems
    };
  };

  if (selectedStudentId) {
    const student = students.find(s => s.id === selectedStudentId)!;
    const cls = classes.find(c => c.id === student.classId)!;

    if (student.approvalStatus === 'pending') {
      return (
        <div className="h-full flex flex-col items-center justify-center p-8 bg-gray-50 text-center relative">
          <button onClick={() => { setSelectedStudentId(null); setInitialTabForChild('homework'); setInitialChatSubTabForChild('group'); }} className="absolute top-6 left-6 text-gray-400 hover:bg-gray-100 p-2 rounded-full">
            <span className="material-icons-round">arrow_back</span>
          </button>
          <div className="bg-yellow-100 p-6 rounded-full mb-6 animate-pulse">
            <span className="material-icons-round text-5xl text-yellow-600">hourglass_empty</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('request_pending')}</h2>
          <p className="text-gray-600 max-w-xs">{t('pending_prompt', { childName: student.childName, className: cls.name })}</p>
        </div>
      );
    }
    
    if (student.approvalStatus === 'rejected') {
       return (
         <div className="h-full flex flex-col items-center justify-center p-8 bg-gray-50 text-center relative">
           <button onClick={() => { setSelectedStudentId(null); setInitialTabForChild('homework'); setInitialChatSubTabForChild('group'); }} className="absolute top-6 left-6 text-gray-400 hover:bg-gray-100 p-2 rounded-full">
             <span className="material-icons-round">arrow_back</span>
           </button>
           <span className="material-icons-round text-5xl text-red-400 mb-4">block</span>
           <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('request_declined')}</h2>
           <p className="text-gray-600 max-w-xs">{t('declined_prompt', { childName: student.childName, className: cls.name })}</p>
         </div>
       );
    }

    const studentOrg = orgs.find(o => o.id === cls?.orgId) || myOrg;

    return <ParentChildView 
      student={student}
      cls={cls}
      user={user}
      onBack={() => { setSelectedStudentId(null); setInitialTabForChild('homework'); setInitialChatSubTabForChild('group'); }}
      onLogout={onLogout}
      onUpdateUser={onUpdateUser}
      allMessages={allMessages}
      allAnnouncements={allAnnouncements.filter(a => a.orgId === (studentOrg?.id || activeOrgId))}
      onSendMessage={onSendMessage}
      onUpdateMessage={onUpdateMessage}
      onDeleteMessage={onDeleteMessage}
      allUsers={allUsers}
      setViewingImageUrl={setViewingImageUrl}
      activeYear={activeYear}
      myStudentsInActiveYear={myStudentsInActiveYear}
      allLogs={allLogs}
      onSaveWeeklyLog={onSaveWeeklyLog}
      onMarkMessagesRead={onMarkMessagesRead}
      allHomeworks={allHomeworks}
      studentHomeworkStatuses={studentHomeworkStatuses}
      onUpdateStudentHomeworkStatus={onUpdateStudentHomeworkStatus}
      onLogStuck={onLogStuck}
      attendanceRecords={attendanceRecords}
      onReportAbsence={(absence, isDelete) => onReportAbsence(absence, student.id, isDelete)}
      calendarEvents={calendarEvents.filter(e => e.orgId === (studentOrg?.id || activeOrgId))}
      t={t}
      tutorLogs={tutorLogs}
      onGetTutorHelp={onGetTutorHelp}
      onTranslateTutorInteraction={onTranslateTutorInteraction}
      familyConnectPrompts={familyConnectPrompts}
      allKudos={allKudos}
      org={studentOrg}
      onSendKudo={onSendKudo}
      onDeleteAccount={onDeleteAccount}
      allStudents={allStudents}
      assessmentEvents={assessmentEvents}
      assessmentResults={assessmentResults}
      preReportedAbsences={preReportedAbsences}
      stuckLogs={stuckLogs}
      initialTab={initialTabForChild}
      initialChatSubTab={initialChatSubTabForChild}
      seenMap={seenMap}
      onMarkTabSeen={markTabSeen}
    />;
  }

  return (
    <>
      <div className="flex flex-col h-full bg-gray-50 relative">
        <Header
            title={myOrg ? myOrg.name : t('my_children')}
            subtitle={myOrg ? undefined : undefined}
            user={user}
            onLogout={onLogout}
            onEditProfile={() => setIsEditingProfile(true)}
            orgLogoUrl={myOrg?.photoUrl}
            onShowHelp={() => setShowHelp(true)}
            onShowPolicy={() => setShowPolicy(true)}
        >
            {activeOrgId && <OrgSwitcher orgs={parentOrgs} activeOrgId={activeOrgId} onSwitchOrg={handleSwitchOrg} user={user} />}
            <button onClick={() => setShowAddChildModal(true)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full cursor-pointer">
                <span className="material-icons-round">person_add</span>
            </button>
        </Header>
        <WelcomeBanner user={user} />
        <div className="p-4 flex-1 overflow-y-auto">
          {myStudentsInActiveYear.length > 0 && isParentPolicyEnabled(myOrg, 'enableFamilyConnect', 'familyConnect') && (
            <FamilyConnectWidget
              user={user}
              t={t}
              prompts={familyConnectPrompts.filter(p => p.parentId === user.id)}
              onGenerate={onGenerateFamilyConnectPrompt}
            />
          )}
          {students.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="bg-green-100 p-6 rounded-full mb-4">
                <span className="material-icons-round text-5xl text-green-500">escalator_warning</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800">{t('no_children_added')}</h3>
              <p className="text-sm text-gray-500 max-w-xs mt-2 mb-6">{t('add_child_prompt')}</p>
              <button 
                onClick={() => setShowAddChildModal(true)}
                className="bg-indigo-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <span className="material-icons-round">add_circle</span>
                {t('add_child')}
              </button>
            </div>
          ) : (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-3 px-1">
                <h3 className="font-bold text-lg text-gray-800">{t('my_children')}</h3>
                <span className="text-xs text-gray-500 font-medium">Click any card or activity badge to view details</span>
              </div>
              <div className="grid gap-4">
                {myStudentsInActiveOrg.map(student => {
                  const cls = classes.find(c => c.id === student.classId)!;
                  const studentOrg = orgs.find(o => o.id === cls?.orgId) || myOrg;
                  const statusColor = {
                    pending: 'border-orange-400',
                    approved: 'border-indigo-500',
                    rejected: 'border-red-400',
                    removed: 'border-gray-400',
                    graduated: 'border-blue-400',
                  }[student.approvalStatus];
                  
                  const act = getStudentActivity(student, studentOrg);
                  const isInteractive = student.approvalStatus !== 'removed' && student.approvalStatus !== 'graduated';

                  return (
                    <div 
                      key={student.id} 
                      onClick={() => isInteractive && handleOpenChild(student.id, 'homework')}
                      className={`bg-white p-4 rounded-2xl shadow-xs border-l-4 ${statusColor} hover:shadow-md transition-all text-left flex flex-col gap-3.5 ${
                        isInteractive ? 'cursor-pointer hover:border-indigo-600' : 'opacity-70'
                      }`}
                    >
                      {/* Top Row: Avatar, Child Identity, Status, Menu */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar 
                            photoUrl={student.photoUrl} 
                            name={student.childName} 
                            size="md" 
                            unread={act.unreadDirectCount > 0 || act.unreadGroupCount > 0 || act.activeStuckCount > 0} 
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-bold text-lg text-gray-800 truncate">{student.childName}</h3>
                              {act.totalActivityItems > 0 && (
                                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 inline-flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-ping"></span>
                                  {act.totalActivityItems} {act.totalActivityItems === 1 ? 'new update' : 'updates'}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 truncate">{cls ? cls.name : 'Unknown Class'}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full capitalize ${
                            student.approvalStatus === 'approved' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                            student.approvalStatus === 'pending' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {student.approvalStatus}
                          </span>
                          {isInteractive && (
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenu 
                                onEdit={() => setEditingStudent(student)} 
                                onDelete={() => {
                                  if (window.confirm(t('confirm_delete_child', { childName: student.childName }))) {
                                      onDeleteStudent(student.id);
                                  }
                                }} 
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Bottom Row: Micro-Chip Activity Indicators (Interactive Deep Links) */}
                      {student.approvalStatus === 'approved' && (
                        <div className="pt-2 border-t border-gray-100 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          {/* 1. Direct Message Alert */}
                          {act.unreadDirectCount > 0 && (
                            <button
                              type="button"
                              onClick={() => handleOpenChild(student.id, 'chat', 'direct')}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors shadow-2xs cursor-pointer animate-pulse"
                            >
                              <span className="material-icons-round text-sm text-rose-500">forum</span>
                              <span>{act.unreadDirectCount} Direct Msg{act.unreadDirectCount > 1 ? 's' : ''}</span>
                            </button>
                          )}

                          {/* 2. Class Announcements Alert */}
                          {act.unreadGroupCount > 0 && (
                            <button
                              type="button"
                              onClick={() => handleOpenChild(student.id, 'chat', 'group')}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors shadow-2xs cursor-pointer"
                            >
                              <span className="material-icons-round text-sm text-indigo-500">campaign</span>
                              <span>{act.unreadGroupCount} New Msg{act.unreadGroupCount > 1 ? 's' : ''}</span>
                            </button>
                          )}

                          {/* 3. Stuck on Homework Alert */}
                          {act.activeStuckCount > 0 && (
                            <button
                              type="button"
                              onClick={() => handleOpenChild(student.id, 'homework')}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-amber-50 text-amber-900 border border-amber-300 hover:bg-amber-100 transition-colors shadow-2xs cursor-pointer"
                            >
                              <span className="material-icons-round text-sm text-amber-600">help_outline</span>
                              <span>Stuck on HW ({act.activeStuckCount})</span>
                            </button>
                          )}

                          {/* 4. Pending Homework Tasks */}
                          {act.pendingHwTasks > 0 && (
                            <button
                              type="button"
                              onClick={() => handleOpenChild(student.id, 'homework')}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors shadow-2xs cursor-pointer"
                            >
                              <span className="material-icons-round text-sm text-blue-500">assignment</span>
                              <span>{act.pendingHwTasks} Task{act.pendingHwTasks > 1 ? 's' : ''} Pending</span>
                            </button>
                          )}

                          {/* 5. New Kudos */}
                          {act.recentKudosCount > 0 && (
                            <button
                              type="button"
                              onClick={() => handleOpenChild(student.id, 'kudos')}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors shadow-2xs cursor-pointer"
                            >
                              <span className="material-icons-round text-sm text-amber-500">military_tech</span>
                              <span>{act.recentKudosCount} New Kudo{act.recentKudosCount > 1 ? 's' : ''}</span>
                            </button>
                          )}

                          {/* 6. New Assessment / Exam Results */}
                          {act.recentAssessmentCount > 0 && (
                            <button
                              type="button"
                              onClick={() => handleOpenChild(student.id, 'results')}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 transition-colors shadow-2xs cursor-pointer"
                            >
                              <span className="material-icons-round text-sm text-emerald-600">grade</span>
                              <span>{act.recentAssessmentCount} New Result{act.recentAssessmentCount > 1 ? 's' : ''}</span>
                            </button>
                          )}

                          {/* 7. Attendance Notice Today */}
                          {(act.markedAbsentToday || act.markedLateToday) && (
                            <button
                              type="button"
                              onClick={() => handleOpenChild(student.id, 'calendar')}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-orange-50 text-orange-800 border border-orange-200 hover:bg-orange-100 transition-colors shadow-2xs cursor-pointer"
                            >
                              <span className="material-icons-round text-sm text-orange-600">event_busy</span>
                              <span>{act.markedAbsentToday ? 'Marked Absent Today' : 'Marked Late Today'}</span>
                            </button>
                          )}

                          {/* 8. Weekly Check-in Notice */}
                          {act.isWeeklyCheckinDue && (
                            <button
                              type="button"
                              onClick={() => handleOpenChild(student.id, 'reports')}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors shadow-2xs cursor-pointer"
                            >
                              <span className="material-icons-round text-sm text-purple-600">rate_review</span>
                              <span>Weekly Check-in Due</span>
                            </button>
                          )}

                          {/* 9. Zero activity state */}
                          {act.totalActivityItems === 0 && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium py-1">
                              <span className="material-icons-round text-emerald-500 text-sm">check_circle</span>
                              <span>All caught up • No pending tasks or messages</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {showHelp && (
        <HelpModal role="parent" onClose={() => setShowHelp(false)} />
      )}
      {showPolicy && (
        <PolicyModal onClose={() => setShowPolicy(false)} />
      )}
      {showAddChildModal && (
        <AddChildModal 
            onClose={() => setShowAddChildModal(false)} 
            onAdd={onAddStudent} 
            t={t}
        />
      )}
      {editingStudent && (
          <EditStudentModal
            student={editingStudent}
            onClose={() => setEditingStudent(null)}
            onSave={(id, data) => handleSaveStudent(id, data)}
            t={t}
          />
      )}
      {isEditingProfile && (
          <EditProfileModal
            user={user}
            onClose={() => setIsEditingProfile(false)}
            onSave={onUpdateUser}
            t={t}
            onDeleteAccount={onDeleteAccount}
          />
      )}
    </>
  );
};
